import { fetchProjectIElements } from "@faro-lotv/project-source";
import { isAsyncThunkAction } from "@reduxjs/toolkit";
import { CREDIT_DISPLAY_NAME } from "@src/constants/credit-constants";
import { TEAM_DISPLAY_NAME, TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import { fetchActiveMembers } from "@store/active-members/active-members-slice-thunk";
import { fetchCompanyStats, fetchProjectStats } from "@store/analytics/analytics-slice-thunk";
import { fetchCompanyMemberGroups } from "@store/company-member-groups/company-member-groups-thunk";
import { fetchCreditTransactions } from "@store/credit-transactions/credit-transactions-thunk";
import { fetchGroupDetails, fetchGroupProjectsCount, fetchGroups, removeGroup, removeMemberFromGroup, updateGroupDetails, updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { fetchCompanyMemberDetails, fetchCompanyMembers, removeMemberFromCompany, updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { deleteProject, fetchProjectDetails, fetchProjects, fetchProjectSettings, removeMemberFromProject, updateMemberRoleInProjectSingle, updateProjectDetails, updateProjectSettings } from "@store/projects/projects-slice-thunk";
import { fetchCompaniesAndWorkspaces, fetchCompanyBrandingSettings, fetchCompanyCommunicationSettings, fetchCompanySubscriptions, updateCompanyBrandingSettings, updateCompanyCommunicationSettings } from "@store/sdb-company/sdb-company-slice-thunk";
import { fetchCompanySettings, updateCompanySetting } from "@store/settings/settings-thunks";
import { createTeam, deleteTeam, fetchTeamAsMember, fetchTeamDetails, fetchTeamMembers, fetchTeamIdsOfMember, fetchTeams, removeMembersFromTeam, updateTeamDetails } from "@store/teams/teams-slice-thunk";
import { fetchCurrentUser, getLoggedInUser, logoutUser, setFueFlags } from "@store/user/user-slice-thunk";

/** Creates an error based on the rejected thunk  */
export function createErrorState(action) {
  let title = "An error occurred";
  let uiError = "dialog";
  switch (true) {
    case isAsyncThunkAction(updateProjectDetails)(action):
      title = "Cannot update project details";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchProjectDetails)(action):
      title = "Cannot fetch project details";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchProjectSettings)(action):
      title = "Cannot fetch project settings";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompanySettings)(action):
      title = "Cannot fetch workspace settings";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateProjectSettings)(action):
      title = "Cannot update project settings";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateCompanySetting)(action):
      title = "Cannot update workspace settings";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchProjects)(action):
      title = "Cannot fetch projects";
      uiError = "toast";
      break;
    case isAsyncThunkAction(removeMemberFromProject)(action):
      title = "Cannot remove member from project";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompaniesAndWorkspaces)(action):
      title = "Cannot fetch workspaces";
      uiError = "page";
      break;
    case isAsyncThunkAction(removeMemberFromCompany)(action):
      title = "Cannot remove this member from workspace";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateMemberRoleInWorkspace)(action):
      title = "Cannot change the member role";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompanyMembers)(action):
      title = "Cannot fetch workspace members";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompanyMemberDetails)(action):
      title = "Cannot fetch workspace member details";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCurrentUser)(action):
      title = "Cannot fetch current user";
      uiError = "page";
      break;
    case isAsyncThunkAction(setFueFlags)(action):
      title = "Cannot update the fueFlags";
      uiError = "toast";
      break;
    case isAsyncThunkAction(logoutUser)(action):
      title = "Failed to logout the user";
      uiError = "toast";
      break;
    case isAsyncThunkAction(getLoggedInUser)(action):
      title = "Failed to fetch the logged in user";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchGroups)(action):
      title = "Cannot fetch the groups";
      uiError = "dialog";
      break;
    case isAsyncThunkAction(fetchCompanyMemberGroups)(action):
      title = "Cannot fetch the workspace member groups";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchGroupDetails)(action):
      title = "Cannot fetch group details";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchGroupProjectsCount)(action):
      title = "Cannot fetch the number of projects in the group";
      uiError = "toast";
      break;
    case isAsyncThunkAction(removeGroup)(action):
      title = "Cannot delete group";
      uiError = "toast";
      break;
    case isAsyncThunkAction(removeMemberFromGroup)(action):
      title = "Cannot remove member from group";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateMemberRoleInGroup)(action):
      title = "Cannot change the manager role";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateGroupDetails)(action):
      title = "Cannot update group details";
      uiError = "toast";
      break;
    case isAsyncThunkAction(deleteProject)(action):
      title = "Cannot delete project";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompanyStats)(action):
      title = "Cannot fetch workspace analytics";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchProjectStats)(action):
      title = "Cannot fetch project analytics";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateCompanyBrandingSettings)(action):
      title = "Cannot update workspace branding setting";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompanyBrandingSettings)(action):
      title = "Cannot fetch the workspace branding settings";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchProjectIElements)(action):
      title = "Cannot fetch projects IElements";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompanyCommunicationSettings)(action):
      title = "Cannot fetch the workspace communication settings";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateCompanyCommunicationSettings)(action):
      title = "Cannot update the workspace communication settings";
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateMemberRoleInProjectSingle)(action):
      title = "Cannot update team member role in project";
      uiError = "toast";
      break;
    case isAsyncThunkAction(createTeam)(action):
      title = `Cannot create ${TEAM_DISPLAY_NAME}`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchTeamDetails, fetchTeamAsMember)(action):
      title = `Cannot fetch ${TEAM_DISPLAY_NAME} details`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(updateTeamDetails)(action):
      title = `Cannot update ${TEAM_DISPLAY_NAME} details`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchTeamMembers)(action):
      title = `Cannot fetch ${TEAM_DISPLAY_NAME} members`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(removeMembersFromTeam)(action):
      title = `Cannot remove member from ${TEAM_DISPLAY_NAME}`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(deleteTeam)(action):
      title = `Cannot delete ${TEAM_DISPLAY_NAME}`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchTeams)(action):
      title = `Cannot fetch ${TEAMS_DISPLAY_NAME}`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchTeamIdsOfMember)(action):
      title = `Cannot fetch the ${TEAMS_DISPLAY_NAME} of the member`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCreditTransactions)(action):
      title = `Cannot fetch ${CREDIT_DISPLAY_NAME} transactions`;
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchCompanySubscriptions)(action):
      title = "Cannot fetch the company subscriptions";
      uiError = "toast";
      break;
    case isAsyncThunkAction(fetchActiveMembers)(action):
      title = "Cannot fetch active members";
      uiError = "toast";
      break;
  }
  return {
    id: action.type,
    title,
    error: action.error,
    uiError
  };
}

/** The order of the importance of an error. The higher number, the more important */
const errorImportance = {
  toast: 1,
  dialog: 2,
  page: 3
};

/**
 * Compares errors by their importance reflected with the uiError
 */
export function compareErrorImportance(errorA, errorB) {
  if (errorImportance[errorA.uiError] > errorImportance[errorB.uiError]) {
    return 1;
  }
  if (errorImportance[errorA.uiError] < errorImportance[errorB.uiError]) {
    return -1;
  }
  return 0;
}